import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FiBell, FiPlus } from "react-icons/fi";

import { useCurrentCompanyId } from "hooks/auth";
import { useInsuranceVerification } from "newui/features/Insurance/hooks/useInsuranceVerification";
import {
	getCompanyDocuments,
	getDocumentCategories,
} from "newui/features/Company/company-api";
import type { DocumentCategory } from "newui/features/Company/company-api";
import type { CorporateDocument } from "newui/features/Company/types";
import { companyDocumentKeys } from "./companyDocumentKeys";
import { EditableDocumentCategoryGroup } from "./components/EditableDocumentCategoryGroup";
import { AddDocument } from "components/DocumentForm/AddDocument";
import { ReadOnlyGuard } from "newui/features/ExecutiveView";
import Modal from "newui/components/ui/Modal/Modal";
import { navigate } from "@reach/router";
import SkeletonBase from "newui/components/Skeleton";

interface DocumentsByCategory {
	categoryId: number;
	categoryName: string;
	documents: CorporateDocument[];
}

interface CompanyDocumentsProps {
	profileTypeId?: number;
}

export const CompanyDocuments: React.FC<CompanyDocumentsProps> = ({
	profileTypeId,
}) => {
	const companyId = useCurrentCompanyId();
	const hasInsuranceVerification = useInsuranceVerification();
	const queryClient = useQueryClient();
	const [isAddModalOpen, setIsAddModalOpen] = useState(false);
	const { data: categories = [], isPending: categoriesLoading } = useQuery({
		queryKey: companyDocumentKeys.categories(profileTypeId ?? 0),
		queryFn: () => getDocumentCategories(profileTypeId ?? 0),
		enabled: Boolean(profileTypeId),
	});

	const { data: documents = [], isPending: documentsLoading } = useQuery({
		queryKey: companyDocumentKeys.documents(companyId ?? 0),
		queryFn: () =>
			getCompanyDocuments(companyId as number, hasInsuranceVerification),
		enabled: Boolean(companyId),
	});

	const rootCategories = useMemo(
		() =>
			categories.filter(
				(c: DocumentCategory) =>
					c.parent_id === 0 &&
					(profileTypeId == null || c.exclude_from_profile_id !== profileTypeId)
			),
		[categories, profileTypeId]
	);

	const grouped = useMemo((): DocumentsByCategory[] => {
		if (!rootCategories.length) {
			return [];
		}

		return rootCategories.map((category: DocumentCategory) => {
			const categoryDocuments = documents.filter(
				(doc: CorporateDocument) =>
					doc.category &&
					(doc.category.id === category.id ||
						doc.category.parent_id === category.id)
			);

			return {
				categoryId: category.id,
				categoryName: category.name,
				documents: categoryDocuments,
			};
		});
	}, [documents, rootCategories]);

	const isLoading = categoriesLoading || documentsLoading;

	if (isLoading) {
		return (
			<div className="space-y-6">
				{Array.from({ length: 4 }).map((_, i) => (
					<div key={i}>
						<SkeletonBase height="18px" width="200px" />
						<div className="mt-2">
							<SkeletonBase height="60px" />
						</div>
					</div>
				))}
			</div>
		);
	}

	const notificationPath = "./documents/request-view";

	return (
		<div className="space-y-2">
			{/* Toolbar */}
			<div className="flex items-center justify-between">
				<p className="text-xs text-gray-500">
					Control visibility of documents by toggling Public or Private.
				</p>

				<div className="flex items-center gap-3">
					<button
						type="button"
						onClick={() => navigate(notificationPath)}
						className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
						data-testid="document-requests"
					>
						<FiBell size={16} />
						Document Requests
					</button>

					<ReadOnlyGuard>
					<button
						type="button"
						onClick={() => setIsAddModalOpen(true)}
						className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700"
						data-testid="add-document"
					>
						<FiPlus size={16} />
						Add New Document
					</button>
					</ReadOnlyGuard>
				</div>
			</div>

			{/* Category groups */}
			<div className="space-y-6">
				{grouped.map((group) => (
					<EditableDocumentCategoryGroup
						key={group.categoryId}
						categoryId={group.categoryId}
						companyId={companyId as number}
						documents={group.documents}
						hasInsuranceVerification={hasInsuranceVerification}
						title={group.categoryName}
					/>
				))}
			</div>

			{/* Add Document Modal */}
			{isAddModalOpen && (
				<Modal
					isOpen={isAddModalOpen}
					onClose={() => {
						setIsAddModalOpen(false);
						if (companyId) {
							queryClient.invalidateQueries({
								queryKey: companyDocumentKeys.documents(companyId),
							});
						}
					}}
					title="Add New Document"
					iconType="create"
					size="xl"
				>
					<AddDocument
						onSuccessfulSubmit={() => {
							setIsAddModalOpen(false);
							if (companyId) {
								queryClient.invalidateQueries({
									queryKey: companyDocumentKeys.documents(companyId),
								});
							}
						}}
					/>
				</Modal>
			)}
		</div>
	);
};
